import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useTenant } from './TenantContext';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { debugLog } from '../lib/debugLog';
import { edgeFetch, createAbortScope } from '../lib/edgeFetch';

function storageKey(tenantId) {
  return `skinny_settings${tenantId ? '_' + tenantId : ''}`;
}

const SettingsContext = createContext(null);

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com','yahoo.com','outlook.com','hotmail.com','icloud.com','aol.com',
  'protonmail.com','proton.me','mail.com','zoho.com','yandex.com','live.com',
  'msn.com','me.com','mac.com','inbox.com','fastmail.com','tutanota.com','gmx.com','gmx.net',
]);

function extractDomainFromEmail(email) {
  if (!email) return '';
  const match = email.toLowerCase().match(/@([^@\s>]+)$/);
  const domain = match ? match[1] : '';
  // Don't return free email domains — they make nonsensical site URLs
  if (FREE_EMAIL_DOMAINS.has(domain)) return '';
  return domain;
}

function extractDomainFromUrl(url) {
  try {
    const u = new URL(url);
    return u.hostname.toLowerCase();
  } catch (_) {
    return '';
  }
}

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const { tenantId, siteId } = useTenant() || { tenantId: '', siteId: '' };
  const hasLoadedRef = useRef(false);
  const lastLoadedTenantRef = useRef('');
  const currentTenantRef = useRef('');
  const prevTenantRef = useRef(tenantId);
  const abortScopeRef = useRef(createAbortScope());
  useEffect(() => { currentTenantRef.current = tenantId || ''; }, [tenantId]);
  
  // Initialize siteUrl based on user's email domain (blank for free email providers)
  const defaultDomain = user?.email ? extractDomainFromEmail(user.email) : '';
  const defaultSiteUrl = defaultDomain ? `https://${defaultDomain}` : '';
  
  const [siteUrl, setSiteUrl] = useState(defaultSiteUrl);
  const [startingUrl, setStartingUrl] = useState(defaultSiteUrl);
  const [doNotCrawlUrls, setDoNotCrawlUrls] = useState([]);
  const [criticalInstructions, setCriticalInstructions] = useState('');
  const [tone, setTone] = useState('');
  const [primaryFacts, setPrimaryFacts] = useState('');
  const [mySkinnyStatus, setMySkinnyStatus] = useState('Pending');
  const [skinnyName, setSkinnyName] = useState('');
  const [skinnyApiKey, setSkinnyApiKey] = useState('');
  const [crawlDepth, setCrawlDepth] = useState(null);
  const [crawlFrequencyDays, setCrawlFrequencyDays] = useState(null);
  const [brandColor, setBrandColor] = useState('');
  const [brandTextColor, setBrandTextColor] = useState('');
  const [useSitemapSeed, setUseSitemapSeed] = useState(true);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [notificationRecipients, setNotificationRecipients] = useState('');
  const [serverEmailNotificationsEnabled, setServerEmailNotificationsEnabled] = useState(false);
  const [serverNotificationRecipients, setServerNotificationRecipients] = useState('');
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [hydratedSiteId, setHydratedSiteId] = useState('');
  const [tenantSites, setTenantSites] = useState([]);
  const [tenantBilling, setTenantBilling] = useState(null);
  const [tenantLimits, setTenantLimits] = useState(null);
  const [runtimeFlags, setRuntimeFlags] = useState(null);

  // Clear stale data synchronously during render when tenant changes,
  // so child components (SiteSwitcherInline) never see old-tenant sites.
  if (prevTenantRef.current !== tenantId) {
    debugLog('[Settings] tenant changed during render, clearing stale data', { from: prevTenantRef.current, to: tenantId });
    prevTenantRef.current = tenantId;
    if (settingsReady) setSettingsReady(false);
    if (tenantSites.length > 0) setTenantSites([]);
  }

  // Update siteUrl when user changes
  useEffect(() => {
    debugLog('[Settings] User effect - user:', user, 'email:', user?.email);
    if (user?.email) {
      const domain = extractDomainFromEmail(user.email);
      const newSiteUrl = `https://${domain}`;
      debugLog('[Settings] Computed domain:', domain, 'newSiteUrl:', newSiteUrl);
      
      // Only update if not already set from localStorage
      const stored = localStorage.getItem(storageKey(tenantId || ''));
      const parsedStored = stored ? JSON.parse(stored) : null;
      debugLog('[Settings] Stored settings:', parsedStored);
      
      const storedUrl = parsedStored?.siteUrl || '';
      const storedDomain = storedUrl ? extractDomainFromUrl(storedUrl) : '';
      const isExample = storedUrl.includes('example.com');
      const mismatch = storedDomain && storedDomain !== domain;
      const shouldOverride = (!stored || !storedUrl || isExample || mismatch);

      if (shouldOverride) {
        debugLog('[Settings] Overriding siteUrl to computed domain:', newSiteUrl, { isExample, mismatch, storedUrl });
        setSiteUrl(newSiteUrl);
        setStartingUrl(newSiteUrl);
      } else {
        debugLog('[Settings] Keeping existing siteUrl:', storedUrl);
      }
    }
  }, [user, tenantId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey(tenantId || ''));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.siteUrl) setSiteUrl(parsed.siteUrl);
        if (parsed.startingUrl) setStartingUrl(parsed.startingUrl);
        if (Array.isArray(parsed.doNotCrawlUrls)) setDoNotCrawlUrls(parsed.doNotCrawlUrls);
        if (typeof parsed.criticalInstructions === 'string') setCriticalInstructions(parsed.criticalInstructions);
        if (typeof parsed.tone === 'string') setTone(parsed.tone);
        if (typeof parsed.primaryFacts === 'string') setPrimaryFacts(parsed.primaryFacts);
        if (typeof parsed.mySkinnyStatus === 'string') setMySkinnyStatus(parsed.mySkinnyStatus);
        if (typeof parsed.skinnyName === 'string') setSkinnyName(parsed.skinnyName);
        if (typeof parsed.skinnyApiKey === 'string') setSkinnyApiKey(parsed.skinnyApiKey);
        if (typeof parsed.crawlDepth === 'number') setCrawlDepth(parsed.crawlDepth);
        if (typeof parsed.crawlFrequencyDays === 'number') setCrawlFrequencyDays(parsed.crawlFrequencyDays);
        if (typeof parsed.useSitemapSeed === 'boolean') setUseSitemapSeed(parsed.useSitemapSeed);
        if (typeof parsed.sitemapUrl === 'string') setSitemapUrl(parsed.sitemapUrl);
      }
    } catch (_) {
      // ignore corrupt storage
    }
  }, [tenantId]);

  useEffect(() => {
    if (!settingsReady) return;
    const payload = JSON.stringify({
      siteUrl,
      startingUrl,
      doNotCrawlUrls,
      criticalInstructions,
      tone,
      primaryFacts,
      mySkinnyStatus,
      skinnyName,
      skinnyApiKey,
      crawlDepth,
      crawlFrequencyDays,
      useSitemapSeed,
      sitemapUrl,
      tenant_id: tenantId || '',
      emailNotificationsEnabled,
      notificationRecipients
    });
    localStorage.setItem(storageKey(tenantId || ''), payload);
  }, [
    settingsReady,
    tenantId,
    siteUrl,
    startingUrl,
    doNotCrawlUrls,
    criticalInstructions,
    tone,
    primaryFacts,
    mySkinnyStatus,
    skinnyName,
    skinnyApiKey,
    crawlDepth,
    crawlFrequencyDays,
    useSitemapSeed,
    sitemapUrl,
    emailNotificationsEnabled,
    notificationRecipients
  ]);

  // Server hydration: fetch per-tenant/site settings; refetch when tenant or site changes
  useEffect(() => {
    (async () => {
      debugLog('[Settings] Fetch effect: start', { tenantId, siteId, currentTenantRef: currentTenantRef.current });
      if (!user?.id) return;
      if (hasLoadedRef.current && lastLoadedTenantRef.current === `${tenantId || ''}:${siteId || ''}`) return;
      hasLoadedRef.current = true;
      const targetTenant = tenantId || '';
      // If the tenant changed since last successful load, discard the current siteId —
      // it may belong to the previous tenant and would cause a 403 forbidden_site error.
      const lastKey = lastLoadedTenantRef.current || '';
      const lastTenant = lastKey ? lastKey.split(':')[0] : '';
      const tenantSwitched = lastTenant && targetTenant !== lastTenant;
      const targetSite = tenantSwitched ? '' : (siteId || '');
      const signal = abortScopeRef.current.renew();
      setSettingsReady(false);
      setSettingsError('');
      setHydratedSiteId('');
      // Clear site-level fields on every refetch
      setMySkinnyStatus('Pending');
      setSiteUrl('');
      setStartingUrl('');
      setDoNotCrawlUrls([]);
      setCriticalInstructions('');
      setTone('');
      setPrimaryFacts('');
      setCrawlDepth(null);
      setCrawlFrequencyDays(null);
      setBrandColor('');
      setBrandTextColor('');
      setUseSitemapSeed(true);
      setSitemapUrl('');
      setEmailNotificationsEnabled(false);
      setNotificationRecipients('');
      // Only clear tenant-level fields when the tenant actually changed
      if (tenantSwitched) {
        debugLog('[Settings] clearing tenant-level data for tenant switch');
        setSkinnyApiKey('');
        setTenantName('');
        setTenantSites([]);
        setTenantBilling(null);
        setTenantLimits(null);
        setRuntimeFlags(null);
      }
      try {
        const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;
        const anon = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY || '';
        let token = (await supabase.auth.getSession()).data.session?.access_token || '';
        for (let i = 0; !token && i < 10; i++) {
          await new Promise(r => setTimeout(r, 150));
          token = (await supabase.auth.getSession()).data.session?.access_token || '';
        }
        if (!token) {
          setSettingsError('Not authenticated. Please sign in.');
          return;
        }
        const headers = { 'content-type': 'application/json' };
        if (anon) headers['apikey'] = anon;
        headers['Authorization'] = `Bearer ${token}`;
        const url = `${base}/functions/v1/settings-get${targetTenant ? `?tenant_id=${targetTenant}` : ''}${targetSite ? `&site_id=${targetSite}` : ''}`;
        debugLog('[Settings] Fetch settings', { url, targetTenant, targetSite });
        const res = await edgeFetch(url, { method: 'GET', headers, signal });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          debugLog('[Settings] Fetch failed', { status: res.status, body: body.slice(0, 200) });
          setSettingsError(`Failed to load settings (${res.status}). Try switching sites or refreshing.`);
          return;
        }
        const data = await res.json();
        // Guard against race: ignore if tenant changed while we were fetching
        if (currentTenantRef.current !== targetTenant) { debugLog('[Settings] Skip apply: tenant changed mid-flight', { current: currentTenantRef.current, targetTenant });
          return; }
        lastLoadedTenantRef.current = `${targetTenant}:${targetSite}`;
        const site = data?.site;
        if (site) {
          if (site.site_url) setSiteUrl(site.site_url);
          if (site.starting_url) setStartingUrl(site.starting_url);
          if (Array.isArray(site.do_not_crawl_urls)) setDoNotCrawlUrls(site.do_not_crawl_urls);
          if (typeof site.critical_instructions === 'string') setCriticalInstructions(site.critical_instructions);
          if (typeof site.tone === 'string') setTone(site.tone);
          if (typeof site.primary_facts === 'string') setPrimaryFacts(site.primary_facts);
          if (typeof site.crawl_depth === 'number') setCrawlDepth(site.crawl_depth);
          if (typeof site.crawl_frequency_days === 'number') setCrawlFrequencyDays(site.crawl_frequency_days);
          if (typeof site.brand_color === 'string') setBrandColor(site.brand_color);
          if (typeof site.brand_text_color === 'string') setBrandTextColor(site.brand_text_color);
          if (typeof site.use_sitemap_seed === 'boolean') setUseSitemapSeed(site.use_sitemap_seed);
          if (typeof site.sitemap_url === 'string') setSitemapUrl(site.sitemap_url || '');
          if (typeof site.email_notifications_enabled === 'boolean') setEmailNotificationsEnabled(!!site.email_notifications_enabled);
          if (Array.isArray(site.notification_recipients)) setNotificationRecipients(site.notification_recipients.join(', '));
          // Track server values for dirty checks
          setServerEmailNotificationsEnabled(!!site.email_notifications_enabled);
          setServerNotificationRecipients(Array.isArray(site.notification_recipients) ? site.notification_recipients.join(', ') : '');
        }
        if (data?.tenant_public_api_key) setSkinnyApiKey(data.tenant_public_api_key);
        if (typeof data?.tenant_name === 'string') {
          const name = data.tenant_name || '';
          setTenantName(name);
        }
        const newSites = Array.isArray(data?.sites) ? data.sites : [];
        debugLog('[Settings] apply', { tenantId: targetTenant, siteId: targetSite, sitesCount: newSites.length });
        setTenantSites(newSites);
        setTenantBilling(data?.tenant_billing || null);
        setTenantLimits(data?.tenant_limits || null);
        setRuntimeFlags(data?.runtime?.flags || null);
        setSettingsReady(true);
        setHydratedSiteId(targetSite);
        // Bind site.status → MySkinny status (read-only display)
        if (data?.site && typeof data.site.status === 'string') {
          const s = String(data.site.status).toLowerCase();
          const map = { pending: 'Pending', active: 'Active', paused: 'Paused', archived: 'Archived' };
          setMySkinnyStatus(map[s] || 'Pending');
        }
        debugLog('[Settings] Applied settings for tenant', { tenantId: targetTenant });
      } catch (e) {
        if (e?.name === 'AbortError') { debugLog('[Settings] Fetch aborted (navigation)'); return; }
        debugLog('[Settings] Hydration error', e);
        setSettingsError('Failed to load settings. Please refresh the page.');
      }
    })();
  }, [tenantId, siteId, user?.id]);

  const addDoNotCrawl = useCallback((url) => {
    setDoNotCrawlUrls(prev => prev.includes(url) ? prev : [...prev, url]);
  }, []);

  const updateDoNotCrawl = useCallback((index, url) => {
    setDoNotCrawlUrls(prev => prev.map((v, i) => i === index ? url : v));
  }, []);

  const removeDoNotCrawl = useCallback((index) => {
    setDoNotCrawlUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const value = useMemo(() => ({
    siteUrl,
    startingUrl,
    doNotCrawlUrls,
    setSiteUrl,
    setStartingUrl,
    setDoNotCrawlUrls,
    criticalInstructions,
    setCriticalInstructions,
    tone,
    setTone,
    primaryFacts,
    setPrimaryFacts,
    mySkinnyStatus,
    setMySkinnyStatus,
    skinnyName,
    setSkinnyName,
    skinnyApiKey,
    setSkinnyApiKey,
    crawlDepth,
    crawlFrequencyDays,
    setCrawlDepth,
    setCrawlFrequencyDays,
    brandColor,
    setBrandColor,
    brandTextColor,
    setBrandTextColor,
    useSitemapSeed,
    setUseSitemapSeed,
    sitemapUrl,
    setSitemapUrl,
    emailNotificationsEnabled,
    setEmailNotificationsEnabled,
    notificationRecipients,
    setNotificationRecipients,
    serverEmailNotificationsEnabled,
    serverNotificationRecipients,
    setServerEmailNotificationsEnabled,
    setServerNotificationRecipients,
      tenantName,
      setTenantName,
      settingsReady,
      settingsError,
      hydratedSiteId,
    tenantSites,
    tenantBilling,
    tenantLimits,
    runtimeFlags,
    addDoNotCrawl,
    updateDoNotCrawl,
    removeDoNotCrawl,
  }), [siteUrl, startingUrl, doNotCrawlUrls, criticalInstructions, tone, primaryFacts, mySkinnyStatus, skinnyName, skinnyApiKey, crawlDepth, crawlFrequencyDays, brandColor, brandTextColor, useSitemapSeed, sitemapUrl, emailNotificationsEnabled, notificationRecipients, serverEmailNotificationsEnabled, serverNotificationRecipients, tenantName, settingsReady, settingsError, hydratedSiteId, tenantSites, tenantBilling, tenantLimits, runtimeFlags, addDoNotCrawl, updateDoNotCrawl, removeDoNotCrawl]);

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
